import { useEffect, useMemo, useRef, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Home, Users, Heart, Mountain, ScrollText, Lasso, BookOpen, Dna, Globe, AlertTriangle, TrendingUp, Search, Star, Skull, Upload, PlusCircle } from "lucide-react";
import { MarkingLayers } from "@/components/game/MarkingLayers";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (p) => (p ? (p.startsWith("http") ? p : `${BACKEND}${p}`) : null);
const familyTint = (fam) => ({ Chestnut: "#9c5a2c", Bay: "#6b4423", Black: "#2b2622" }[fam] || "#7a5230");
const sexWord = (s) => (s === "stallion" ? "Stallion" : s === "mare" ? "Mare" : s);

export const HorseThumb = ({ h, size = 40 }) => (
  h?.art_image ? (
    <div className="relative shrink-0 overflow-hidden rounded-lg" style={{ width: size, height: size, background: "#f5efe6" }}>
      <img src={imgUrl(h.art_image)} alt={h.color || "coat"} className="absolute inset-0 h-full w-full object-contain" style={{ zIndex: 1 }} />
      <MarkingLayers base={h.art_image} layers={h.overlays} compact={size < 96} />
    </div>
  ) : h?.art_missing ? (
    <span className="grid shrink-0 place-items-center rounded-lg border border-dashed border-stone-300 bg-stone-100 text-center text-[8px] font-bold leading-tight text-stone-500" style={{ width: size, height: size }} title="Artwork not available yet">
      No art yet
    </span>
  ) : (
    <span className="grid shrink-0 place-items-center rounded-lg text-[10px] font-bold text-white/90" style={{ width: size, height: size, background: h?.color_hex || familyTint(h?.base_family) }}>
      {h?.sex === "mare" ? "♀" : "♂"}
    </span>
  )
);

const Loading = () => <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</div>;

// ============================ DASHBOARD HOME ============================
const STATUS_STYLE = { "Healthy": "text-emerald-600", "Approaching Capacity": "text-amber-600", "Over Capacity": "text-rose-600" };
const NAV = [
  ["herd", "Herd", Users], ["bands", "Bands", Users], ["fertility", "Fertility", Heart],
  ["land", "Land / Capacity", Mountain], ["reports", "Annual Reports", ScrollText],
  ["roundups", "Roundups", Lasso], ["gatherbook", "Gather Book", BookOpen],
  ["genetics", "Genetics", Dna], ["about", "About HMAs", BookOpen], ["introduce", "Introduce Stallion", PlusCircle], ["public", "Public Profile", Globe],
];

export const AboutView = ({ isAdmin }) => {
  const [text, setText] = useState(null);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => { gameApi.get("/game/hma/about").then(({ data }) => { setText(data.text || ""); setDraft(data.text || ""); }); }, []);
  const save = async () => { try { await gameApi.put("/game/admin/hma/about", { text: draft }); setText(draft); setEdit(false); toast.success("Saved"); } catch (e) { toast.error("Failed"); } };
  if (text === null) return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-3" data-testid="hma-about-view">
      {!edit ? (
        <div className="rounded-2xl border border-border bg-card p-4">
          {text ? <p className="whitespace-pre-wrap text-sm leading-relaxed text-primary">{text}</p> : <p className="text-sm text-muted-foreground">No description added yet.</p>}
          {isAdmin && <button data-testid="hma-about-edit" onClick={() => setEdit(true)} className="mt-3 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground">Edit description</button>}
        </div>
      ) : (
        <div className="space-y-2 rounded-2xl border border-border bg-card p-4">
          <textarea data-testid="hma-about-textarea" value={draft} onChange={(e) => setDraft(e.target.value)} rows={12} className="w-full rounded-lg border border-border bg-background p-2 text-sm" placeholder="Explain how HMAs work — founding, bands, breeding, roundups, genetics…" />
          <div className="flex gap-2">
            <button data-testid="hma-about-save" onClick={save} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground">Save</button>
            <button onClick={() => { setDraft(text); setEdit(false); }} className="rounded-lg border border-border px-3 py-1.5 text-xs font-bold">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const IntroduceView = ({ onDone }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState({});
  const load = () => gameApi.get("/game/hma/introduce-options").then(({ data }) => setData(data)).catch(() => setData({ sources: [], next_cost: 0 }));
  useEffect(() => { load(); }, []);
  const introduce = async (id) => {
    setBusy(id);
    try {
      const { data: r } = await gameApi.post("/game/hma/introduce-stallion", { model_id: id });
      toast.success(`Introduced #${r.stallion.hma_number}${r.cost ? ` for ${r.cost.toLocaleString()} coins` : " (free)"}!`);
      onDone && onDone(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy("");
  };
  if (!data) return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  const cost = data.next_cost;
  return (
    <div className="space-y-3" data-testid="hma-introduce-view">
      <div className="rounded-xl border border-border bg-card p-3 text-xs leading-relaxed text-muted-foreground">
        Introduce a new <span className="font-bold text-primary">3-year-old bachelor stallion</span> from any pack you own. He joins with no mares and gets a one-time first-year edge competing for mares next rollover — a fresh bloodline for your herd.
        <span className="ml-1 font-bold text-primary">Next introduction: {cost ? `${cost.toLocaleString()} coins` : "FREE"}</span>
      </div>
      {data.sources.map((src) => (
        <div key={src.key || src.label} className="space-y-2">
          <p className="text-xs font-bold text-primary">{src.label}</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {src.models.map((m) => (
              <div key={m.id} data-testid={`introduce-model-${m.id}`} className="rounded-xl border border-border bg-card p-3">
                <p className="text-sm font-bold">{m.display_name}</p>
                <p className="text-[11px] text-muted-foreground">{m.base_color || "—"} · {m.body_model}</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">New genetic traits this stallion could introduce</p>
                <div className="mt-1 flex flex-wrap gap-1">{(m.traits && m.traits.length) ? m.traits.map((t) => <span key={t} className="rounded-full bg-secondary/15 px-2 py-0.5 text-[10px] font-bold text-secondary">{t}</span>) : <span className="text-[10px] text-muted-foreground">Solid / base coat</span>}</div>
                {m.genotype && m.genotype.length > 0 && (
                  <>
                    <button data-testid={`introduce-genotype-toggle-${m.id}`} onClick={() => setOpen((o) => ({ ...o, [m.id]: !o[m.id] }))} className="mt-2 flex items-center gap-1 text-[11px] font-bold text-primary hover:underline">
                      <Dna size={12} /> {open[m.id] ? "Hide genotype" : "View genotype"}
                    </button>
                    {open[m.id] && (
                      <div data-testid={`introduce-genotype-${m.id}`} className="mt-1.5 space-y-1 rounded-lg bg-muted/40 p-2">
                        <p className="text-[10px] leading-snug text-muted-foreground">His allele pairs — this is exactly what he can pass to foals (each foal inherits one allele from each pair).</p>
                        {m.genotype.map((row) => (
                          <div key={row.locus} className="flex items-center justify-between gap-2 text-[11px]">
                            <span className={`truncate ${row.expresses ? "font-bold text-foreground" : "text-muted-foreground"}`}>{row.label}</span>
                            <span className="flex shrink-0 items-center gap-1.5">
                              <span className="rounded bg-background px-1.5 py-0.5 font-mono text-[10px] font-bold">{row.pair}</span>
                              <span className="w-28 shrink-0 truncate text-right text-[10px] text-muted-foreground" title={row.note}>{row.note}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <button data-testid={`introduce-btn-${m.id}`} disabled={busy === m.id} onClick={() => introduce(m.id)} className="mt-2 w-full rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-50">{busy === m.id ? "Introducing…" : (cost ? `Introduce · ${cost.toLocaleString()} coins` : "Introduce · FREE")}</button>
              </div>
            ))}
          </div>
        </div>
      ))}
      {(!data.sources || data.sources.length === 0) && <p className="p-4 text-sm text-muted-foreground">No owned packs available.</p>}
    </div>
  );
};

export const HMAHome = ({ go }) => {
  const [d, setD] = useState(null);
  useEffect(() => { gameApi.get("/game/hma/dashboard").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return <Loading />;
  const c = d.capacity, comp = d.composition, lt = d.lifetime;
  return (
    <div className="space-y-3" data-testid="hma-home">
      {d.hma.cover_image && <img src={imgUrl(d.hma.cover_image)} alt="cover" className="h-28 w-full rounded-xl object-cover" />}
      <div>
        <h2 className="font-display text-xl font-bold text-primary">{d.hma.name}</h2>
        <p className="text-[11px] text-muted-foreground">{d.hma.id} · Year {d.current_year}{d.hma.description ? ` · ${d.hma.description}` : ""}</p>
      </div>

      {/* alerts */}
      {d.alerts.length > 0 && (
        <div className="space-y-1.5" data-testid="hma-alerts">
          {d.alerts.map((a) => (
            <div key={a.kind} data-testid={`hma-alert-${a.kind}`} className={`flex items-start gap-2 rounded-lg border p-2 text-xs ${a.level === "warn" ? "border-rose-300 bg-rose-50 text-rose-800" : "border-sky-300 bg-sky-50 text-sky-800"}`}>
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span><b>{a.title}</b> — {a.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* population status */}
      <div className="rounded-xl border border-border bg-card p-3" data-testid="hma-population">
        <div className="flex items-center justify-between">
          <p className={`text-sm font-bold ${STATUS_STYLE[d.population_status]}`}>{d.population_status}</p>
          <p className="text-xs text-muted-foreground">{c.open_slots} open slots</p>
        </div>
        <div className="mt-1 grid grid-cols-3 gap-2 text-center text-xs">
          <div><p className="text-lg font-bold text-primary">{c.population}</p><p className="text-muted-foreground">Population</p></div>
          <div><p className="text-lg font-bold text-primary">{c.carrying_capacity}</p><p className="text-muted-foreground">Capacity</p></div>
          <div><p className={`text-lg font-bold ${c.over_capacity ? "text-rose-600" : "text-primary"}`}>{c.pct_of_capacity}%</p><p className="text-muted-foreground">Of capacity</p></div>
        </div>
      </div>

      {/* composition */}
      <div className="grid grid-cols-3 gap-1.5 text-center text-xs sm:grid-cols-6" data-testid="hma-composition">
        {[["Bands", comp.bands], ["Bachelors", comp.bachelors], ["Mares", comp.breeding_mares], ["Foals", comp.foals], ["Treated", comp.treated], ["No Sire", d.no_sire_count]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-border bg-card py-1.5"><p className="text-base font-bold text-primary">{v}</p><p className="text-[10px] text-muted-foreground">{l}</p></div>
        ))}
      </div>

      {/* roundup + gather quick summary */}
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="hma-home-roundups" onClick={() => go("roundups")} className="rounded-xl border border-border bg-card p-3 text-left text-xs">
          <p className="flex items-center gap-1 font-bold text-primary"><Lasso size={13} /> Roundups</p>
          <p className="mt-1 text-muted-foreground">{d.roundup.available ? "Available now" : d.roundup.reason}</p>
          {d.roundup.last_size != null && <p className="text-[11px] text-muted-foreground">Last: {d.roundup.last_size} gathered</p>}
        </button>
        <button data-testid="hma-home-gatherbook" onClick={() => go("gatherbook")} className="rounded-xl border border-border bg-card p-3 text-left text-xs">
          <p className="flex items-center gap-1 font-bold text-primary"><BookOpen size={13} /> Gather Book</p>
          <p className="mt-1 text-muted-foreground">Holding {d.gather.in_holding} · Makeover {d.gather.in_makeover}</p>
          <p className="text-[11px] text-muted-foreground">Kept {d.gather.kept} · Sold {d.gather.sold}</p>
        </button>
      </div>

      {/* nav grid */}
      <div className="grid grid-cols-3 gap-2" data-testid="hma-nav">
        {NAV.map(([k, l, Icon]) => (
          <button key={k} data-testid={`hma-nav-${k}`} onClick={() => go(k)} className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card py-3 text-[11px] font-bold text-primary">
            <Icon size={18} /> {l}
          </button>
        ))}
      </div>

      {/* activity feed */}
      {d.activity.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-3" data-testid="hma-activity">
          <p className="mb-1 text-xs font-bold text-primary">Recent Activity</p>
          {d.activity.map((a, i) => <p key={i} className="text-[11px] text-muted-foreground">Y{a.year} · {a.text}</p>)}
        </div>
      )}

      {/* lifetime stats */}
      <details className="rounded-xl border border-border p-2 text-xs" data-testid="hma-lifetime">
        <summary className="cursor-pointer font-bold text-primary">Lifetime HMA Statistics</summary>
        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
          <span>Foals produced: {lt.total_foals}</span><span>Deaths: {lt.total_deaths}</span>
          <span>Gathered: {lt.gathered}</span><span>Released: {lt.released}</span>
          <span>Makeover horses: {lt.makeover_produced}</span><span>Kept: {lt.kept} · Sold: {lt.sold}</span>
          <span>Oldest ever: {lt.oldest_age}</span><span></span>
          {lt.top_sire && <span className="col-span-2">Top sire: {lt.top_sire} ({lt.top_sire_count})</span>}
          {lt.top_dam && <span className="col-span-2">Top dam: {lt.top_dam} ({lt.top_dam_count})</span>}
        </div>
      </details>
    </div>
  );
};

// ============================ HERD BROWSER ============================
const FILTERS = [
  ["all", "All"], ["stallion", "Stallions"], ["mare", "Mares"], ["foal", "Foals"], ["yearling", "Yearlings"],
  ["breeding", "Breeding age"], ["senior", "Seniors"], ["band_stallion", "Band Stallions"],
  ["bachelor", "Bachelors"], ["unassigned", "Unassigned"], ["treated", "Fertility Treated"], ["deceased", "Deceased"],
];
export const HerdBrowser = ({ onOpen }) => {
  const [horses, setHorses] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("hma_number");
  const [q, setQ] = useState("");
  const [color, setColor] = useState("all");
  const [trait, setTrait] = useState("all");
  const [body, setBody] = useState("all");
  const incDec = filter === "deceased";

  useEffect(() => { gameApi.get(`/game/hma/horses?include_deceased=${incDec}`).then((r) => setHorses(r.data.horses)); }, [incDec]);
  const colors = useMemo(() => [...new Set((horses || []).map((h) => h.base_color).filter(Boolean))], [horses]);
  const bodies = useMemo(() => [...new Set((horses || []).map((h) => h.body_model).filter(Boolean))], [horses]);
  const traits = useMemo(() => [...new Set((horses || []).flatMap((h) => h.traits || []))], [horses]);

  const shown = useMemo(() => {
    let list = (horses || []).filter((h) => {
      if (filter === "stallion" && h.sex !== "stallion") return false;
      if (filter === "mare" && h.sex !== "mare") return false;
      if (filter === "foal" && h.age >= 3) return false;
      if (filter === "yearling" && h.age !== 1) return false;
      if (filter === "breeding" && !(h.age >= 3 && h.age <= 20)) return false;
      if (filter === "senior" && h.age <= 20) return false;
      if (filter === "band_stallion" && h.band_status !== "band_stallion") return false;
      if (filter === "bachelor" && h.band_status !== "bachelor") return false;
      if (filter === "unassigned" && h.band_status !== "unassigned") return false;
      if (filter === "treated" && !h.fertility_treated) return false;
      if (filter === "deceased" && h.alive) return false;
      if (color !== "all" && h.base_color !== color) return false;
      if (body !== "all" && h.body_model !== body) return false;
      if (trait !== "all" && !(h.traits || []).includes(trait)) return false;
      if (q && !`${h.hma_number} ${h.nickname || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
    const key = { hma_number: (a, b) => a.hma_number - b.hma_number, age: (a, b) => b.age - a.age,
      sex: (a, b) => (a.sex || "").localeCompare(b.sex), color: (a, b) => (a.base_color || "").localeCompare(b.base_color),
      band: (a, b) => (a.band_status || "").localeCompare(b.band_status), birth: (a, b) => (a.birth_year || 0) - (b.birth_year || 0) };
    return [...list].sort(key[sort] || key.hma_number);
  }, [horses, filter, color, body, trait, q, sort]);

  if (!horses) return <Loading />;
  return (
    <div className="space-y-2" data-testid="hma-herd-browser">
      <input data-testid="herd-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search HMA # or nickname…" className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-xs" />
      <div className="flex gap-1 overflow-x-auto pb-1">
        {FILTERS.map(([k, l]) => <button key={k} data-testid={`herd-filter-${k}`} onClick={() => setFilter(k)} className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${filter === k ? "bg-primary text-primary-foreground" : "border border-border"}`}>{l}</button>)}
      </div>
      <div className="flex flex-wrap gap-1 text-[11px]">
        <select data-testid="herd-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1"><option value="hma_number">HMA ID</option><option value="age">Age</option><option value="sex">Sex</option><option value="color">Color</option><option value="band">Band</option><option value="birth">Birth year</option></select>
        <select value={color} onChange={(e) => setColor(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1"><option value="all">Any color</option>{colors.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={body} onChange={(e) => setBody(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1"><option value="all">Any body</option>{bodies.map((b) => <option key={b} value={b}>{b}</option>)}</select>
        {traits.length > 0 && <select value={trait} onChange={(e) => setTrait(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1"><option value="all">Any trait</option>{traits.map((t) => <option key={t} value={t}>{t}</option>)}</select>}
      </div>
      <p className="text-[11px] text-muted-foreground">{shown.length} horse(s)</p>
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {shown.map((h) => (
          <button key={h.id} data-testid={`herd-horse-${h.hma_number}`} onClick={() => onOpen(h.id)} className={`flex items-center gap-2 rounded-lg border border-border bg-card p-2 text-left text-xs ${!h.alive ? "opacity-60" : ""}`}>
            <HorseThumb h={h} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold">{h.label} {!h.alive && <Skull size={11} className="inline text-muted-foreground" />}</p>
              <p className="truncate text-[11px] text-muted-foreground">{h.base_color} · {sexWord(h.sex)} · age {h.age}</p>
              <div className="mt-0.5 flex flex-wrap gap-1">
                {h.band_status === "band_stallion" && <Badge>Band Stallion</Badge>}
                {h.band_status === "bachelor" && <Badge>Bachelor</Badge>}
                {h.sex === "mare" && h.age >= 3 && h.age <= 20 && <Badge>Breeding</Badge>}
                {h.band_status === "unassigned" && <Badge>Unassigned</Badge>}
                {h.fertility_treated && <Badge tone="rose">Treated</Badge>}
                {h.age > 20 && <Badge tone="amber">Senior</Badge>}
                {h.age < 3 && <Badge tone="sky">Youngstock</Badge>}
                {(h.traits || []).map((t) => <Badge key={t} tone="violet">{t}</Badge>)}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const Badge = ({ children, tone }) => {
  const t = { rose: "bg-rose-100 text-rose-700", amber: "bg-amber-100 text-amber-700", sky: "bg-sky-100 text-sky-700", violet: "bg-violet-100 text-violet-700" }[tone] || "bg-primary/10 text-primary";
  return <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${t}`}>{children}</span>;
};

// ============================ BANDS + BACHELORS ============================
export const BandsView = ({ onOpen }) => {
  const [d, setD] = useState(null);
  useEffect(() => { gameApi.get("/game/hma/bands").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  return (
    <div className="space-y-3" data-testid="hma-bands">
      {d.bands.length === 0 && <p className="text-xs text-muted-foreground">No breeding bands yet.</p>}
      {d.bands.map((b) => (
        <div key={b.stallion.id} data-testid={`band-${b.stallion.hma_number}`} className="rounded-xl border border-border bg-card p-2">
          <button onClick={() => onOpen(b.stallion.id)} className="flex w-full items-center gap-2 text-left">
            <HorseThumb h={b.stallion} size={44} />
            <div className="min-w-0 flex-1 text-xs"><p className="truncate font-bold text-primary">{b.stallion.label} · Band Stallion</p><p className="text-[11px] text-muted-foreground">{b.stallion.phenotype?.base_color} · age {b.stallion.age} · {b.mare_count} mares · {b.foal_count} foals</p></div>
          </button>
          <div className="mt-1.5 space-y-1" data-testid={`band-family-${b.stallion.hma_number}`}>
            {b.mares.length === 0 && <p className="text-[10px] text-muted-foreground">No mares in this band.</p>}
            {b.mares.map((m) => (
              <div key={m.id} data-testid={`band-mare-${m.hma_number}`} className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-2 py-1 text-[11px]">
                <button onClick={() => onOpen(m.id)} className="font-bold text-primary underline-offset-2 hover:underline">{m.label}</button>
                <span className="text-muted-foreground">sire: {m.sire ? <button onClick={() => m.sire_id && onOpen(m.sire_id)} className="underline-offset-2 hover:underline" title={m.sire_alive ? "" : "deceased"}>{m.sire}{!m.sire_alive ? " †" : ""}</button> : "founder"}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div>
        <p className="mb-1 text-xs font-bold text-primary">Bachelor Stallions ({d.bachelors.length})</p>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {d.bachelors.map((s) => (
            <button key={s.id} data-testid={`bachelor-${s.hma_number}`} onClick={() => onOpen(s.id)} className="flex items-center gap-2 rounded-lg border border-border bg-card p-2 text-left text-xs">
              <HorseThumb h={s} />
              <div className="min-w-0 flex-1"><p className="truncate font-bold">{s.label}</p><p className="text-[11px] text-muted-foreground">{s.phenotype?.base_color} · age {s.age}</p><div className="flex gap-1">{s.newly_eligible && <Badge tone="sky">New at 3</Badge>}<Badge>{s.status_label}</Badge>{s.former_band_stallion && <Badge tone="amber">Former Band Stallion</Badge>}</div></div>
            </button>
          ))}
        </div>
      </div>
      {d.unassigned_mares.length > 0 && (
        <div><p className="mb-1 text-xs font-bold text-primary">Unassigned Mares ({d.unassigned_mares.length})</p>
          <div className="flex flex-wrap gap-1">{d.unassigned_mares.map((m) => <button key={m.id} onClick={() => onOpen(m.id)} className="rounded-full border border-border px-2 py-0.5 text-[10px]">{m.label}</button>)}</div></div>
      )}
    </div>
  );
};

// ============================ FERTILITY ============================
export const FertilityView = ({ onChanged }) => {
  const [d, setD] = useState(null);
  const load = () => gameApi.get("/game/hma/fertility").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (m) => {
    if (!window.confirm(m.treated ? "Remove fertility treatment for this year?" : "Apply fertility treatment for this game year? It expires after the breeding cycle.")) return;
    try { const { data } = await gameApi.post(`/game/hma/horses/${m.id}/fertility`); toast.success(data.treated ? "Treatment scheduled" : "Treatment removed"); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!d) return <Loading />;
  const s = d.summary;
  return (
    <div className="space-y-3" data-testid="hma-fertility">
      <div className="grid grid-cols-2 gap-1.5 text-center text-xs sm:grid-cols-4" data-testid="fertility-summary">
        {[["Breeding age", s.breeding_age], ["Untreated", s.untreated], ["Treated", s.treated], ["No sire", s.no_unrelated_sire]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-border bg-card py-1.5"><p className="text-base font-bold text-primary">{v}</p><p className="text-[10px] text-muted-foreground">{l}</p></div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">Untreated eligible mare ≈ 80% base foal chance; treated ≈ 20% (before population penalties). Estimates only — outcomes are random. {s.over_capacity ? `Herd is ${s.pct_over_capacity}% over capacity, reducing births.` : ""}</p>
      {s.no_unrelated_sire > 0 && <div className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800"><AlertTriangle size={14} className="mt-0.5" /><span>{s.no_unrelated_sire} mare(s) currently have no unrelated eligible sire and will not produce foals this year.</span></div>}
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {d.mares.map((m) => (
          <div key={m.id} data-testid={`fertility-mare-${m.hma_number}`} className="flex items-center gap-2 rounded-lg border border-border bg-card p-2 text-xs">
            <HorseThumb h={m} />
            <div className="min-w-0 flex-1"><p className="truncate font-bold">{m.label}</p><p className="text-[11px] text-muted-foreground">{m.phenotype?.base_color} · age {m.age}{m.band_stallion ? ` · band: ${m.band_stallion}` : " · unassigned"}</p>{!m.can_breed && <Badge tone="rose">No unrelated sire</Badge>}</div>
            <button data-testid={`fertility-toggle-${m.hma_number}`} onClick={() => toggle(m)} className={`shrink-0 rounded-lg px-2 py-1 text-[11px] font-bold ${m.treated ? "bg-rose-100 text-rose-700" : "bg-primary text-primary-foreground"}`}>{m.treated ? "Remove" : "Treat"}</button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================ LAND / CAPACITY ============================
export const LandView = ({ onChanged }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const load = () => gameApi.get("/game/hma").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const upgrade = async () => {
    setBusy(true);
    try { const { data } = await gameApi.post("/game/hma/capacity/upgrade"); toast.success(`Capacity now ${data.carrying_capacity}`); setConfirm(false); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  if (!d) return <Loading />;
  const c = d.capacity;
  return (
    <div className="space-y-3" data-testid="hma-land">
      <div className="rounded-xl border border-border bg-card p-3 text-center text-xs">
        <p className="text-2xl font-bold text-primary">{c.carrying_capacity}</p>
        <p className="text-muted-foreground">Current carrying capacity · {c.population} in herd · {c.open_slots} open</p>
        <p className="mt-1 text-[11px] text-muted-foreground">Tiers: 15 → 25 → 40 → 55 → 75 → 100 → 125 → 150 → 200 (max)</p>
      </div>
      {c.next_tier ? (
        <button data-testid="land-upgrade-btn" onClick={() => setConfirm(true)} className="flex w-full items-center justify-center gap-1 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground"><TrendingUp size={16} /> Expand to {c.next_tier} — {c.next_tier_price?.toLocaleString()}🪙</button>
      ) : <p className="text-center text-xs font-bold text-emerald-600">Maximum capacity reached (200).</p>}
      {confirm && c.next_tier && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4" onClick={() => setConfirm(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-4 text-sm" onClick={(e) => e.stopPropagation()} data-testid="land-confirm">
            <p className="font-display text-lg font-bold text-primary">Expand carrying capacity?</p>
            <p className="mt-1 text-xs text-muted-foreground">Upgrade from {c.carrying_capacity} to {c.next_tier} for {c.next_tier_price?.toLocaleString()}🪙. This is permanent.</p>
            <div className="mt-3 flex gap-2"><button onClick={() => setConfirm(false)} className="flex-1 rounded-lg border border-border py-2 text-xs font-bold">Cancel</button>
              <button data-testid="land-confirm-btn" disabled={busy} onClick={upgrade} className="flex-1 rounded-lg bg-primary py-2 text-xs font-bold text-primary-foreground disabled:opacity-50">{busy ? "Purchasing…" : "Confirm"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================ GENETICS SUMMARY ============================
export const GeneticsView = () => {
  const [d, setD] = useState(null);
  useEffect(() => { gameApi.get("/game/hma/genetics-summary").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  const bar = (dist) => Object.entries(dist).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-3" data-testid="hma-genetics-summary">
      <p className="text-xs text-muted-foreground">{d.population} living horses</p>
      {d.warnings.length > 0 && (
        <div className="space-y-1" data-testid="genetics-warnings">
          {d.warnings.map((w, i) => <div key={i} className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"><AlertTriangle size={13} className="mt-0.5" /><span>{w}</span></div>)}
        </div>
      )}
      <Section title="Base Phenotype">{bar(d.phenotype_distribution).map(([k, v]) => <Row key={k} k={k} v={v} tot={d.population} />)}</Section>
      <Section title="Body Model">{bar(d.body_distribution).map(([k, v]) => <Row key={k} k={k} v={v} tot={d.population} />)}</Section>
      <Section title="Pattern & Dilution Genes">{d.genes.map((g) => <Row key={g.allele} k={g.trait} v={g.count} tot={d.population} />)}</Section>
    </div>
  );
};
const Section = ({ title, children }) => <div className="rounded-xl border border-border bg-card p-3"><p className="mb-1.5 text-xs font-bold text-primary">{title}</p><div className="space-y-1">{children}</div></div>;
const Row = ({ k, v, tot }) => (
  <div className="text-[11px]"><div className="flex justify-between"><span>{k}</span><span className="font-bold">{v}</span></div>
    <div className="mt-0.5 h-1.5 w-full rounded bg-muted"><div className="h-full rounded bg-primary" style={{ width: `${tot ? (v / tot) * 100 : 0}%` }} /></div></div>
);

// ============================ SETTINGS / PUBLIC PROFILE ============================
export const SettingsView = ({ onChanged }) => {
  const [d, setD] = useState(null);
  const [desc, setDesc] = useState("");
  const [horses, setHorses] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const load = async () => {
    const { data } = await gameApi.get("/game/hma/dashboard");
    setD(data); setDesc(data.hma.description || "");
    const h = await gameApi.get("/game/hma/horses"); setHorses(h.data.horses);
  };
  useEffect(() => { load(); }, []);
  const save = async (patch) => { try { await gameApi.post("/game/hma/settings", patch); toast.success("Saved"); load(); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const upCover = async (file) => {
    if (!file) return; setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); await gameApi.post("/game/hma/cover", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Cover updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } setBusy(false);
  };
  if (!d) return <Loading />;
  const featured = d.hma.featured_ids || [];
  const toggleFeat = (id) => { const next = featured.includes(id) ? featured.filter((x) => x !== id) : (featured.length < 6 ? [...featured, id] : featured); save({ featured_ids: next }); };
  return (
    <div className="space-y-3" data-testid="hma-settings">
      <div className="rounded-xl border border-border p-3">
        <p className="text-xs font-bold text-primary">Visibility</p>
        <div className="mt-1 flex gap-2">
          {["public", "private"].map((v) => <button key={v} data-testid={`hma-privacy-${v}`} onClick={() => save({ privacy: v })} className={`flex-1 rounded-lg py-1.5 text-xs font-bold ${d.hma.privacy === v ? "bg-primary text-primary-foreground" : "border border-border"}`}>{v === "public" ? "Public" : "Private"}</button>)}
        </div>
      </div>
      <div className="rounded-xl border border-border p-3">
        <p className="text-xs font-bold text-primary">Cover & Description</p>
        {d.hma.cover_image && <img src={imgUrl(d.hma.cover_image)} alt="cover" className="mt-1 h-24 w-full rounded-lg object-cover" />}
        <label data-testid="hma-cover-upload" className={`mt-1 flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-dashed border-primary py-1.5 text-xs font-bold text-primary ${busy ? "opacity-50" : ""}`}><Upload size={13} /> Upload cover<input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upCover(e.target.files?.[0])} /></label>
        <textarea data-testid="hma-desc" value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={() => desc !== d.hma.description && save({ description: desc })} placeholder="Describe your HMA…" className="mt-2 w-full rounded-lg border border-border bg-background px-2 py-1.5 text-xs" />
      </div>
      <div className="rounded-xl border border-border p-3">
        <p className="text-xs font-bold text-primary">Featured Horses ({featured.length}/6)</p>
        <div className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
          {horses.map((h) => <button key={h.id} data-testid={`hma-feature-${h.hma_number}`} onClick={() => toggleFeat(h.id)} className={`flex items-center gap-2 rounded-lg border p-1.5 text-left text-[11px] ${featured.includes(h.id) ? "border-primary bg-primary/10" : "border-border"}`}><HorseThumb h={h} size={28} /><span className="min-w-0 flex-1 truncate">{h.label}</span>{featured.includes(h.id) && <Star size={12} className="fill-primary text-primary" />}</button>)}
        </div>
      </div>
    </div>
  );
};

export const PublicProfile = ({ playerNumber, hmaId }) => {
  const [d, setD] = useState(null);
  useEffect(() => { const url = hmaId ? `/game/hma/public/by-hma/${hmaId}` : `/game/hma/public/${playerNumber}`; gameApi.get(url).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [playerNumber, hmaId]);
  if (!d) return <Loading />;
  if (d.error) return <p className="p-4 text-center text-xs text-muted-foreground">HMA not found.</p>;
  if (d.private) return <p className="p-4 text-center text-xs text-muted-foreground"><b>{d.name}</b> by {d.owner} is private.</p>;
  return (
    <div className="space-y-3" data-testid="hma-public-profile">
      {d.cover_image && <img src={imgUrl(d.cover_image)} alt="cover" className="h-28 w-full rounded-xl object-cover" />}
      <div><h2 className="font-display text-xl font-bold text-primary">{d.name}</h2><p className="text-[11px] text-muted-foreground">{d.id} · by {d.owner} · Pop {d.population}/{d.carrying_capacity}</p>{d.description && <p className="mt-1 text-xs">{d.description}</p>}</div>
      <p className="text-[11px] text-muted-foreground">Foundation: {(d.sources || []).join(", ")} · Gathered {d.gathered} · Makeover horses {d.makeover_produced}</p>
      {d.featured.length > 0 && (
        <div><p className="mb-1 text-xs font-bold text-primary">Featured Horses</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{d.featured.map((h) => <div key={h.id} className="rounded-lg border border-border bg-card p-2 text-center text-[11px]"><HorseThumb h={h} size={56} /><p className="mt-1 truncate font-bold">{h.label}</p><p className="text-muted-foreground">{h.phenotype?.base_color}</p></div>)}</div></div>
      )}
    </div>
  );
};
